import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Chip,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { createBlogPost, updateBlogPost, getBlogPostById } from '../services/blogService';
import { useAuth } from '../context/AuthContext';
import ReactQuill from 'react-quill';
import FileUpload from '../components/FileUpload';
import DashboardLayout from '../components/DashboardLayout';
import 'react-quill/dist/quill.snow.css';

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isEditing = !!id; // id will be undefined for /blog/new route

  const [formData, setFormData] = useState({
    title: '',
    content: '<h1>Headline 1</h1>\n<h2>Headline 2</h2>\n',
    tags: [],
    featuredImage: '',
    published: true
  });

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagInputError, setTagInputError] = useState('');

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link', 'image'
  ];

  useEffect(() => {
    // Redirect if not authenticated
    if (!isAuthenticated) {
      navigate('/signin');
      return;
    }

    // If editing, fetch the post data
    if (isEditing) {
      const fetchPost = async () => {
        try {
          setLoading(true);
          // Fetch the post by ID
          const data = await getBlogPostById(id);

          setFormData({
            title: data.title,
            content: data.content,
            tags: data.tags || [],
            featuredImage: data.featuredImage || '',
            published: data.published
          });

          // Set the attachments
          if (data.attachments && data.attachments.length > 0) {
            setAttachments(data.attachments);
          }

          setLoading(false);
        } catch (err) {
          console.error('Error fetching blog post:', err);
          setError('Failed to load blog post for editing.');
          setLoading(false);
        }
      };

      fetchPost();
    }
  }, [id, isEditing, isAuthenticated, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content
    });
  };

  const handleAddTag = () => {
    const tag = newTag.trim();

    if (!tag) {
      setTagInputError('Tag cannot be empty');
      return;
    }

    if (formData.tags.includes(tag)) {
      setTagInputError('Tag already exists');
      return;
    }

    setFormData({
      ...formData,
      tags: [...formData.tags, tag]
    });

    setNewTag('');
    setTagInputError('');
  };

  const handleDeleteTag = (tagToDelete) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToDelete)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.content.trim()) {
      setError('Content is required');
      return;
    }

    try {
      setLoading(true);
      // Include attachments in the post data
      const postData = {
        ...formData,
        attachments: attachments
      };

      if (isEditing) {
        await updateBlogPost(id, postData);
      } else {
        await createBlogPost(postData);
      }

      setSuccess(true);
      setError(null);

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/blog');
      }, 1500);
    } catch (err) {
      console.error('Error saving blog post:', err);
      setError('Failed to save blog post. Please try again later.');
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return null; // We'll redirect in useEffect
  }

  // Debug log to see if component is rendering
  console.log('BlogEditor rendering - isEditing:', isEditing, 'id:', id, 'current path:', window.location.pathname);

  return (
    <DashboardLayout title={isEditing ? 'Edit Blog Post' : 'Create New Blog Post'} maxWidth="md">
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Button component={RouterLink} to="/blog" sx={{ mb: 2 }}>
            ← Back to Blog
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 4 }}>
            {isEditing ? 'Blog post updated successfully!' : 'Blog post created successfully!'}
          </Alert>
        )}

        {loading && !success ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              margin="normal"
              required
            />



            <Box sx={{ my: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                Content
              </Typography>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={modules}
                formats={formats}
                style={{ height: '300px', marginBottom: '50px' }}
              />
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* File Upload Component */}
            <FileUpload
              postId={id}
              attachments={attachments}
              setAttachments={setAttachments}
              disabled={loading || success}
            />

            <Divider sx={{ my: 4 }} />

            <Box sx={{ mt: 6, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Add tag"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  error={!!tagInputError}
                  helperText={tagInputError}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  sx={{ mr: 1 }}
                />
                <Button onClick={handleAddTag} variant="outlined">
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleDeleteTag(tag)}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ mt: 3, mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.published}
                    onChange={handleChange}
                    name="published"
                  />
                }
                label="Publish immediately"
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                component={RouterLink}
                to="/blog"
                variant="contained"
                disabled={loading}
                data-cancel="true"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
              >
                {isEditing ? 'Update Post' : 'Create Post'}
              </Button>
            </Box>
          </Paper>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default BlogEditor; 